import { Coins } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { TOKEN_PACKAGES } from "@/config/plans";
import type { TokenPackage } from "@/config/plans";

interface TokenPacksProps {
  /** Charge the selected pack — wired to usePricing().handlePurchaseTokens. */
  onPurchase: (tokens: number, priceNIS: number) => void;
  /** Disable all buttons while a payment is being created. */
  disabled?: boolean;
  /** Currency symbol (PayPlus charges ILS, so always ₪ today). */
  currencySymbol: string;
  /** Discounted price to charge for a pack. */
  getPriceDisplay: (pkg: TokenPackage) => number;
  /** Original (pre-discount) price, shown struck through. */
  getOriginalPriceDisplay: (pkg: TokenPackage) => number;
}

/**
 * The one-time token-pack purchase list. Shared between the Pricing page's
 * "Add Tokens" dialog and the credit-wall upgrade modal so both render the
 * exact same packs and pricing.
 */
export function TokenPacks({
  onPurchase,
  disabled,
  currencySymbol,
  getPriceDisplay,
  getOriginalPriceDisplay,
}: TokenPacksProps) {
  const { t } = useLanguage();

  return (
    <div className="space-y-4">
      {TOKEN_PACKAGES.map((pkg) => (
        <button
          key={pkg.tokens}
          onClick={() => onPurchase(pkg.tokens, pkg.priceNIS)}
          disabled={disabled}
          className="w-full p-4 rounded-xl border border-border bg-card hover:border-brand/50 hover:bg-brand/5 transition-all flex items-center justify-between group disabled:opacity-50 disabled:cursor-not-allowed relative"
        >
          {/* Discount badge */}
          <span className="absolute -top-2 end-3 text-[10px] font-black px-2 py-0.5 rounded-full bg-green-500 text-white">
            -20%
          </span>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center border border-brand/20">
              <Coins className="w-5 h-5 text-brand" />
            </div>
            <div className="text-start">
              <span className="font-bold text-foreground text-lg">
                {pkg.tokens.toLocaleString()}
              </span>
              <span className="text-muted-foreground text-sm block">
                {t.pricing.tokensLabel}
              </span>
            </div>
          </div>
          <div className="text-end">
            <span className="text-sm text-muted-foreground/60 line-through block">
              {currencySymbol}
              {getOriginalPriceDisplay(pkg)}
            </span>
            <span className="font-black text-xl text-green-400">
              {currencySymbol}
              {getPriceDisplay(pkg)}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}
